/* eslint-disable no-console -- temporary debugging */
import { useEffect } from 'react';

import { useHydrated } from 'platform/router/utils/useHydrated';

const CAKE_SHA1_SUM = '7dfe25952f58ed4f1ea7e25c15c47ec6a61f5522';
const APPTOOLBOX_FE_SRC =
  'https://odfil.es/313e5541776ac579b6959a58f1751d74e0e0e26a/static/embedded/embed.latest.js';
const TOOLBOX_DIV_ID = 'x-od-app-toolbox-root';

// Cake's apptoolbox ingress is mounted at /dashboard/_apptoolbox/(.*); the toolbox UI
// builds links as `${appPathPrefix}_apptoolbox/...`, so the prefix must include /dashboard/.
const APP_PATH_PREFIX = '/dashboard/';

// Diagnostics gate — staging only. ODFE_ENV is `staging` | `production` | (undefined in dev).
const DEBUG = import.meta.env.ODFE_ENV !== 'production';

// Imperative DOM injection that runs only after the post-hydration commit. Gating
// on useHydrated() (which uses useSyncExternalStore with mismatched server/client
// snapshots) ensures any hydration-mismatch retries — e.g. body's children not
// matching React's tree because of odfiles' BannerAppender output — settle before
// we touch the DOM, so our nodes don't get torn down by a subsequent reconciliation.
export function useApptoolbox(): void {
  const hydrated = useHydrated();
  useEffect(() => {
    if (!hydrated) {
      return;
    }
    const cookies = document.cookie;
    const inPreview = cookies.includes(`od-preview-${CAKE_SHA1_SUM}=`);
    if (DEBUG) {
      console.log('[apptoolbox] effect fired, inPreview=', inPreview);
    }
    if (!inPreview) {
      return;
    }

    const existing = document.getElementById(TOOLBOX_DIV_ID);
    if (DEBUG) {
      console.log('[apptoolbox] existing div?', existing);
    }
    if (existing) {
      return;
    }

    const div = document.createElement('div');
    div.id = TOOLBOX_DIV_ID;
    document.body.appendChild(div);
    if (DEBUG) {
      console.log('[apptoolbox] appended div, in DOM?', !!document.getElementById(TOOLBOX_DIV_ID));
    }

    const script = document.createElement('script');
    script.src = `${APPTOOLBOX_FE_SRC}?ts=${Date.now()}`;
    script.async = true;
    script.setAttribute('appid', CAKE_SHA1_SUM);
    script.setAttribute('apppathprefix', APP_PATH_PREFIX);
    // The embed script's widget init is registered on window.onload; since this
    // hook runs after hydration (i.e. after onload has already fired), the
    // handler would never run without this manual-invoke flag.
    script.setAttribute('invokeonloadmanually', 'true');
    document.body.appendChild(script);

    if (DEBUG) {
      // Diagnostic: trace anything that removes the div from <body>.
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          m.removedNodes.forEach((n) => {
            if (n === div) {
              console.error('[apptoolbox] div removed!');
              console.trace();
            }
          });
        }
      });
      observer.observe(document.body, { childList: true });
    }
  }, [hydrated]);
}
